import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import moment from 'moment-timezone';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { Grid, IconButton } from '@mui/material';
import Images from '../../../Utils/images';
import { getShiftPreviewData } from '../../../redux/orderSlice';
import { RootState, useAppSelector } from '../../../redux/store';
import CustomButton from '../../../Components/CustomButton';
import EditShiftModal from './EditShiftModal';
import ViewNotesModal from './ViewNotesModal';
import {
  formatPriceBreakupValues,
  getEndDayTimestamp,
  getStartDayTimestamp,
  getTimeWithTimeZoneAbbreviationNew,
} from '../../../Utils/helperFns';
import AdditionalShifts from './AdditionalShifts';
import AdditionalChargesModal from './AdditionalChargesModal';
import { useLocation } from 'react-router-dom';
import ToolTipComponent from '../../../Components/ToolTipComponent';
import { Worksheet } from '../../MyOrders/Components/ShiftDetails';

const SHIFT_DETAILS_CONSOLIDATED_PRICE_TOOLTIP =
  'This consolidated price includes the daily, hourly, and extra-day rates for the shift.';
const SHIFT_DETAILS_CONSOLIDATED_HOURS_TOOLTIP =
  'The consolidated hours does not include fixed cost shifts.';

// Types for better TypeScript support
interface Shift {
  _id: string;
  actualStartTime: string;
  actualEndTime: string;
  duration: number;
  /** Backend sometimes marks flexible-hour shifts at shift-level */
  isFLexibleShiftHr?: boolean;
  deliveryAddress: {
    locationName: string;
    locationPurpose: string[];
    address: string;
  };
  unit?: number;
  cost?: number;
  extraDayPrice?: number;
  isWeeklyShift?: boolean;
  isPendingForVendorApproval?: boolean;
  mileageCost?: number;
  mileageDistance?: number;
  mileageRate?: number;
  perDiemCost?: number;
  rushFee?: number;
  rushFeeApplied?: boolean;
  status?: string;
}

interface CartData {
  _id: string;
  worksheets: Worksheet[];
}

interface ShitDetailsProps {
  cartDetails: any;
  cartData: CartData[];
  totalWorksheets: number;
  isEditMode?: boolean;
  setIsEditMode: (isEditMode: boolean) => void;
  orderDetails: any;
  orderIds: string;
  setCheckbox: React.Dispatch<React.SetStateAction<any[]>>;
  setSelectedDate: (date: any) => void;
  onDeleteShiftsClick?: () => void;
  totalShifts?: number;
}

// Constants
const ITEMS_PER_PAGE = 10;
const DEBOUNCE_DELAY = 300;
const SCROLL_THRESHOLD = 50;
const MAX_HEIGHT = '550px';

// Date formatting for service date
const formatServiceDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Pure function of `worksheet` — safe at module scope
const getWorksheetTotalCost = (worksheet: any) => {
  const shifts = worksheet?.shifts ?? [];
  if (shifts.length === 0) return Number(worksheet?.totalCost) || 0;
  return shifts.reduce(
    (sum: number, s: any) =>
      sum +
      ((s as any).isWeeklyShift
        ? 0
        : Number(s?.cost || s?.extraDayPrice || 0) +
          (s?.mileageCost ?? 0) +
          (s?.perDiemCost ?? 0) +
          (s?.rushFee ?? 0)),
    0,
  );
};

interface WorksheetItemProps {
  worksheet: Worksheet;
  expandedAccordion: string | null;
  isEditMode?: boolean;
  product: any;
  selectedShiftIds: Set<string>;
  filteredShifts: (data: Worksheet[]) => Shift[];
  handleWorksheetSelection: (worksheetId: string, checked: boolean) => void;
  handleToggle: (id: string) => void;
  handleShiftSelection: (
    shiftId: string,
    checked: boolean,
    worksheetId: string,
  ) => void;
  handleNotesModalOpen: (shift: any) => void;
  setAdditionalShifts: React.Dispatch<React.SetStateAction<boolean>>;
  setAdditionalShiftsData: React.Dispatch<React.SetStateAction<any>>;
}

const WorksheetItem = React.memo(
  ({
    worksheet,
    expandedAccordion,
    isEditMode,
    product,
    selectedShiftIds,
    filteredShifts,
    handleWorksheetSelection,
    handleToggle,
    handleShiftSelection,
    handleNotesModalOpen,
    setAdditionalShifts,
    setAdditionalShiftsData,
  }: WorksheetItemProps) => {
    // Calculate if checkbox should be shown
    const today = moment().startOf('day'); // today at 00:00:00
    const firstShiftDate = moment(worksheet?.firstShift).startOf('day');
    const lastShiftDate = moment(worksheet?.lastShift).startOf('day');

    // check if worksheet firstShift or lastShift date is today or in the future
    const isDateInFutureOrToday =
      firstShiftDate.isSameOrAfter(today) || lastShiftDate.isSameOrAfter(today);

    // removed console logs

    return (
      <Accordion
        key={worksheet._id}
        expanded={expandedAccordion === worksheet._id}
        className="ScheduleAccordian"
      >
        <AccordionSummary
          onClick={event => event.stopPropagation()}
          className="ScheduleSummary"
        >
          {/* removed console log */}
          <div className="serviceCard">
            <figure className="flex ">
              {isEditMode &&
                isDateInFutureOrToday &&
                !product?.products?.find(
                  (p: any) =>
                    p.cartId === worksheet.cartId &&
                    p.publishStatus !== 'PUBLISHED',
                ) &&
                moment().isSameOrBefore(moment(worksheet?.lastShift)) && (
                  <div className="checkbox">
                    <input
                      type="checkbox"
                      checked={
                        Array.from(selectedShiftIds)?.filter((val: string) =>
                          worksheet?.shifts?.find(
                            (shift: Shift) => shift._id === val,
                          ),
                        )?.length >= filteredShifts([worksheet])?.length
                      }
                      onChange={e =>
                        handleWorksheetSelection(
                          worksheet._id,
                          e.target.checked,
                        )
                      }
                      ref={el => {
                        if (el) {
                          el.indeterminate =
                            Array.from(selectedShiftIds)?.filter(
                              (val: string) =>
                                worksheet?.shifts?.find(
                                  (shift: Shift) => shift._id === val,
                                ),
                            )?.length < filteredShifts([worksheet])?.length &&
                            Array.from(selectedShiftIds)?.filter(
                              (val: string) =>
                                worksheet?.shifts?.find(
                                  (shift: Shift) => shift._id === val,
                                ),
                            )?.length > 0;
                        }
                      }}
                      onClick={e => e.stopPropagation()}
                    />
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label aria-label="Select worksheet"></label>
                  </div>
                )}
              <img
                src={worksheet.productInfo?.thumbnailImg || Images.AUTH_BG_1}
                alt={worksheet.productInfo?.title || 'Service'}
                loading="lazy"
              />
            </figure>
            <div className="serviceDetails">
              <div className="serviceInfo">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '13px',
                  }}
                >
                  <h2>{worksheet.productInfo?.title || 'Service'}</h2>
                  {worksheet?.rentPricing?.isFlexibleShiftHr && (
                    <div className="chips">
                      <span className="dark-blue">
                        {worksheet?.rentPricing?.minRateModel == 'DAILY'
                          ? 'Daily'
                          : 'Weekly'}
                      </span>
                    </div>
                  )}
                </div>
                <span>
                  <i className="pin-icon"></i>
                  &nbsp;{worksheet?.locationPurpose?.[0]}
                  {worksheet?.locationPurpose?.length > 1 && (
                    <ToolTipComponent
                      theme="light"
                      title={
                        <div>
                          {worksheet?.locationPurpose.map(
                            (purpose: string, index: number) => {
                              if (index == 0) return null;
                              return <div key={index}>{purpose}</div>;
                            },
                          )}
                        </div>
                      }
                    >
                      <span className="cursor-pointer">
                        &nbsp;+ {worksheet?.locationPurpose?.length - 1}
                      </span>
                    </ToolTipComponent>
                  )}
                  {' :'} {worksheet?.locationName}, {worksheet?.address} |{' '}
                  {worksheet?.rentPricing?.isFlexibleShiftHr ? (
                    'Flexible'
                  ) : (
                    <span>
                      {moment(worksheet.firstShift).format('h:mm A')}{' '}
                      {getTimeWithTimeZoneAbbreviationNew(
                        worksheet.firstShift,
                        'z',
                      )}{' '}
                      - {moment(worksheet.lastShift).format('h:mm A')}{' '}
                      {getTimeWithTimeZoneAbbreviationNew(
                        worksheet.lastShift,
                        'z',
                      )}
                    </span>
                  )}
                </span>
              </div>
              <div className="serviceDates">
                <ul>
                  <li>
                    <h3 className="mb6">
                      {' '}
                      {worksheet.totalUnit > 1 ? 'Units' : 'Unit'}
                    </h3>
                    <p className="fw600 MuiTypography-body2">
                      {worksheet.totalUnit}
                    </p>
                  </li>
                  <li>
                    <h3 className="mb6">Duration</h3>
                    <p className="fw600 MuiTypography-body2">
                      {worksheet?.rentPricing?.isFlexibleShiftHr
                        ? 'Flexible'
                        : worksheet?.totalDuration + 'hrs'}
                    </p>
                  </li>
                  <li>
                    <h3 className="mb6">Cost</h3>
                    <p className="txt_primary MuiTypography-body2 d-flex align-center">
                      {worksheet.shifts?.length > 0 &&
                      worksheet.shifts?.every(
                        (s: any) => (s as any).isWeeklyShift,
                      ) ? (
                        <span style={{ color: 'green' }}>Included</span>
                      ) : (
                        <>
                          $
                          {formatPriceBreakupValues(
                            getWorksheetTotalCost(worksheet),
                          )}
                        </>
                      )}
                    </p>
                  </li>
                </ul>

                <IconButton onClick={() => handleToggle(worksheet._id)}>
                  <img
                    src={
                      expandedAccordion === worksheet._id
                        ? Images.Arrow_Up
                        : Images.ARROW_DOWN
                    }
                    alt="toggle"
                  />
                </IconButton>
              </div>
            </div>
          </div>
        </AccordionSummary>

        <AccordionDetails className="preview-details">
          {worksheet.shifts?.map((shift: Shift) => (
            <div key={shift._id} className="locations">
              <div className="serviceCard">
                {/* removed console log */}
                <div className="check-div">
                  {isEditMode &&
                    isDateInFutureOrToday &&
                    moment().isSameOrBefore(moment(worksheet?.lastShift)) &&
                    !product?.products?.find(
                      (p: any) =>
                        p.cartId === worksheet.cartId &&
                        p.publishStatus !== 'PUBLISHED',
                    ) && (
                      <>
                        <div className="checkbox">
                          <input
                            type="checkbox"
                            checked={selectedShiftIds.has(shift._id)}
                            onChange={e =>
                              handleShiftSelection(
                                shift._id,
                                e.target.checked,
                                worksheet._id,
                              )
                            }
                            onClick={e => e.stopPropagation()}
                          />
                          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                          <label aria-label="Select shift"></label>
                        </div>
                      </>
                    )}
                </div>
                <div className="preview-details-row">
                  <Grid container spacing={1} className=" mb-12">
                    <Grid item md={10} xs={12} className="">
                      <h3 className="MuiTypography-body2 ">
                        <p>
                          <i className="pin-icon mr-6"></i>
                          {shift?.deliveryAddress?.locationPurpose?.[0]}
                          {worksheet?.locationPurpose?.length > 1 && (
                            <ToolTipComponent
                              theme="light"
                              title={
                                <div>
                                  {shift?.deliveryAddress?.locationPurpose?.map(
                                    (purpose: string, index: number) => {
                                      if (index == 0) return null;
                                      return <div key={index}>{purpose}</div>;
                                    },
                                  )}
                                </div>
                              }
                            >
                              <span className="cursor-pointer">
                                {' '}
                                +{' '}
                                {shift?.deliveryAddress?.locationPurpose
                                  ?.length - 1}
                              </span>
                            </ToolTipComponent>
                          )}
                          {' :'}{' '}
                          {`${
                            shift?.deliveryAddress?.locationName +
                            ', ' +
                            shift?.deliveryAddress?.address
                          }`}{' '}
                          |{' '}
                          {worksheet?.rentPricing?.isFlexibleShiftHr ? (
                            'Flexible'
                          ) : (
                            <span>
                              {moment(shift?.actualStartTime).format('h:mm A')}{' '}
                              {getTimeWithTimeZoneAbbreviationNew(
                                shift?.actualStartTime,
                                'z',
                              )}{' '}
                              - {moment(shift?.actualEndTime).format('h:mm A')}{' '}
                              {getTimeWithTimeZoneAbbreviationNew(
                                shift?.actualEndTime,
                                'z',
                              )}
                            </span>
                          )}
                        </p>
                      </h3>
                    </Grid>

                    <Grid item md={2} xs={12} className="text-md-right">
                      <button
                        onClick={() => handleNotesModalOpen(shift)}
                        className="text-linking MuiTypography-body2 link-btn"
                      >
                        View Notes
                      </button>
                    </Grid>
                  </Grid>

                  <div className="serviceDates">
                    <ul>
                      <li>
                        <h3 className="mb6">Status</h3>
                        <p className="fw600 MuiTypography-body2">NA</p>
                      </li>
                      <li>
                        <h3 className="mb6">Duration</h3>
                        <p className="fw600 MuiTypography-body2">
                          {worksheet?.rentPricing?.isFlexibleShiftHr
                            ? 'Flexible'
                            : shift?.duration + 'hrs'}
                        </p>
                      </li>
                      <li>
                        <h3 className="mb6">Cost</h3>
                        <p className="txt_primary MuiTypography-body2 d-flex align-center">
                          {(shift as any).isWeeklyShift ? (
                            <>
                              <span style={{ color: 'black' }}>Included</span>
                              <i
                                className="info-icon ml-6 cursor-pointer"
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                  setAdditionalShifts(true);
                                  setAdditionalShiftsData(shift);
                                }}
                                onKeyDown={e => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setAdditionalShifts(true);
                                    setAdditionalShiftsData(shift);
                                  }
                                }}
                                aria-label="View additional charges"
                              />
                            </>
                          ) : (
                            <>
                              $
                              {formatPriceBreakupValues(
                                Number(
                                  (shift as any).cost ||
                                    (shift as any).extraDayPrice ||
                                    0,
                                ) +
                                  ((shift as any).mileageCost ?? 0) +
                                  ((shift as any).perDiemCost ?? 0) +
                                  ((shift as any).rushFee ?? 0),
                              )}
                              &nbsp;
                              <i
                                className="info-icon cursor-pointer"
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                  setAdditionalShifts(true);
                                  setAdditionalShiftsData(shift);
                                }}
                                onKeyDown={e => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setAdditionalShifts(true);
                                    setAdditionalShiftsData(shift);
                                  }
                                }}
                                aria-label="View additional charges"
                              />
                            </>
                          )}
                        </p>
                      </li>
                    </ul>
                  </div>

                  {shift?.status !== 'CANCELLED' &&
                    Object.hasOwn(shift, 'isPendingForVendorApproval') && (
                      <p className="text-warning_500 MuiTypography-body2 d-flex align-center">
                        <i className="info-warning-icon mr-6"></i>
                        {shift?.isPendingForVendorApproval
                          ? 'Approval pending from vendor'
                          : 'Approved by vendor'}
                      </p>
                    )}
                </div>
              </div>
            </div>
          ))}
        </AccordionDetails>
      </Accordion>
    );
  },
);
WorksheetItem.displayName = 'WorksheetItem';

export default function ShiftDetails({
  cartDetails,
  cartData,
  totalWorksheets,
  isEditMode,
  setIsEditMode,
  orderIds,
  setCheckbox,
  setSelectedDate,
  onDeleteShiftsClick,
  totalShifts = 0,
}: ShitDetailsProps) {
  // State management
  const [expandedAccordion, setExpandedAccordion] = useState<string | null>(
    null,
  );
  const [_page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [additionalShifts, setAdditionalShifts] = useState(false);
  // Modal and selection state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedWorksheet, setSelectedWorksheet] = useState<Worksheet | null>(
    null,
  );
  const [editCostState, setEditCostState] = useState({
    editGrandTotalCost: 0,
    editGrandTotalHours: 0,
  });
  const [selectedShifts, setSelectedShifts] = useState<Shift[]>([]);
  const [selectedWorksheetIds, setSelectedWorksheetIds] = useState<any>([]);
  const [additionalShiftsData, setAdditionalShiftsData] = useState<any>({});
  const [additionalChargesOpen, setAdditionalChargesOpen] = useState(false);
  const [additionalChargesShifts, _setAdditionalChargesShifts] = useState<
    any[]
  >([]);
  const [selectedShiftIds, setSelectedShiftIds] = useState<Set<string>>(
    new Set(),
  );

  const { product } = useLocation()?.state;
  // New state for tracking selected worksheet shifts in the specified format
  const [selectedWorksheetShifts, setSelectedWorksheetShifts] = useState<{
    [worksheetId: string]: {
      shifts: Shift[];
      worksheet: Worksheet;
    };
  }>({});

  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [selectedWorksheetForNotes, setSelectedWorksheetForNotes] =
    useState<Worksheet | null>(null);

  // Refs
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<any>(null);

  useEffect(() => {
    // getShiftNotes('id=689a468c23e39b1ff5eea278&type=SHIFT&limit=10&pageNo=1');
  }, []);

  // Redux selectors - optimized
  const { shiftPreviewData, loader } = useAppSelector(
    (state: RootState) => ({
      shiftPreviewData: state.order.shiftPreviewData,
      loader: state.order.loader,
    }),
    (left, right) =>
      left.shiftPreviewData === right.shiftPreviewData &&
      left.loader === right.loader,
  );

  // Memoized computed values
  const displayData = useMemo(
    () => shiftPreviewData?.data?.data || cartData || [],
    [shiftPreviewData?.data?.data, cartData],
  );

  const totalWorksheetsFromApi = useMemo(
    () => shiftPreviewData?.data?.total || totalWorksheets || 0,
    [shiftPreviewData?.data?.total, totalWorksheets],
  );

  /** API-computed total cost across all worksheets in scope (not just the loaded page). */
  const apiTotalEstimateCost: number | undefined =
    shiftPreviewData?.data?.totalEstimateCost;

  /** API-computed total duration (hrs) across all worksheets in scope (not just the loaded page). */
  const apiTotalEstimateDuration: number | undefined =
    shiftPreviewData?.data?.totalEstimateDuration;

  const currentWorksheetCount = useMemo(
    () =>
      displayData.reduce(
        (total: number, cart: CartData) =>
          total + (cart?.worksheets?.length || 0),
        0,
      ),
    [displayData],
  );

  // Helper function to get all shift IDs from a worksheet
  const getWorksheetShiftIds = useCallback((worksheet: Worksheet): string[] => {
    return worksheet.shifts?.map((shift: Shift) => shift._id) || [];
  }, []);

  // Helper function to find a shift by ID within a worksheet
  const findShiftById = useCallback(
    (shiftId: string, worksheetId: string): Shift | null => {
      for (const cart of displayData) {
        const worksheet = cart.worksheets?.find(
          (w: Worksheet) => w._id === worksheetId,
        );
        if (worksheet) {
          const shift = worksheet.shifts?.find((s: Shift) => s._id === shiftId);
          if (shift) return shift;
        }
      }
      return null;
    },
    [displayData],
  );

  // Memoized handlers
  const handleToggle = useCallback((id: string) => {
    setExpandedAccordion((prevId: any) => (prevId === id ? null : id));
  }, []);

  // Handle worksheet selection
  const handleWorksheetSelection = useCallback(
    (worksheetId: string, checked: boolean) => {
      let targetWorksheet: Worksheet | null = null;

      for (const cart of displayData) {
        const found = cart.worksheets?.find(
          (w: Worksheet) => w._id === worksheetId,
        );
        if (found) {
          targetWorksheet = found;
          break;
        }
      }

      if (!targetWorksheet) return;

      //  FILTER OUT EXPIRED SHIFTS (ONLY FOR SELECTION)
      const validShifts =
        targetWorksheet.shifts?.filter((shift: Shift) =>
          moment(shift.actualEndTime).isSameOrAfter(moment()),
        ) || [];

      // If no valid shifts, do nothing
      if (!validShifts.length) return;

      const shiftIds = validShifts.map((shift: Shift) => shift._id);

      setSelectedShiftIds(prev => {
        const newSet = new Set(prev);

        // ---------------- CHECKBOX ----------------
        setCheckbox((prevCheckboxData: any) => {
          const updatedCheckboxData = [...prevCheckboxData];

          const idx = updatedCheckboxData.findIndex(
            entry => entry.itemId === targetWorksheet?.cartId,
          );

          if (checked) {
            if (idx !== -1) {
              const currentIds = new Set(updatedCheckboxData[idx].checkedIds);
              shiftIds.forEach((id: string) => currentIds.add(id));

              updatedCheckboxData[idx] = {
                itemId: targetWorksheet?.cartId,
                checkedIds: Array.from(currentIds),
              };
            } else {
              updatedCheckboxData.push({
                itemId: targetWorksheet?.cartId,
                checkedIds: [...shiftIds],
              });
            }
          } else {
            if (idx !== -1) {
              const filteredIds = updatedCheckboxData[idx].checkedIds.filter(
                (id: string) => !shiftIds.includes(id),
              );

              if (filteredIds.length > 0) {
                updatedCheckboxData[idx] = {
                  itemId: targetWorksheet?.cartId,
                  checkedIds: filteredIds,
                };
              } else {
                updatedCheckboxData.splice(idx, 1);
              }
            }
          }

          return updatedCheckboxData;
        });

        // ---------------- SELECTION ----------------
        if (checked) {
          shiftIds.forEach((id: string) => newSet.add(id));

          setSelectedWorksheetIds((prev: string[]) => [...prev, worksheetId]);

          const nonWeeklyHoursDelta = validShifts
            .filter((s: any) => !s.isWeeklyShift)
            .reduce((sum: number, s: any) => sum + Number(s?.duration ?? 0), 0);
          setEditCostState(prev => ({
            editGrandTotalCost:
              prev.editGrandTotalCost + getWorksheetTotalCost(targetWorksheet!),
            editGrandTotalHours: !targetWorksheet?.rentPricing
              ?.isFlexibleShiftHr
              ? prev.editGrandTotalHours + nonWeeklyHoursDelta
              : prev.editGrandTotalHours,
          }));

          setSelectedWorksheetShifts(prev => {
            const existingKey = Object.keys(prev).find(key => {
              const ws = Object.values(displayData)
                .flatMap((cd: any) => cd.worksheets || [])
                .find(w => w._id === key);
              return ws?.cartId === targetWorksheet?.cartId;
            });

            if (existingKey && prev[existingKey]) {
              const existingShifts = prev[existingKey].shifts;

              const newShifts = validShifts.filter(
                (s: Shift) => !existingShifts.some(es => es._id === s._id),
              );

              return {
                ...prev,
                [existingKey]: {
                  worksheet: prev[existingKey].worksheet,
                  shifts: [...existingShifts, ...newShifts],
                },
              };
            }

            return {
              ...prev,
              [worksheetId]: {
                worksheet: targetWorksheet,
                shifts: validShifts, // ✅ only valid shifts stored
              },
            };
          });
        } else {
          shiftIds.forEach((id: string) => newSet.delete(id));

          setSelectedWorksheetIds((prev: string[]) =>
            prev.filter(id => id !== worksheetId),
          );

          const nonWeeklyHoursDelta = validShifts
            .filter((s: any) => !s.isWeeklyShift)
            .reduce((sum: number, s: any) => sum + Number(s?.duration ?? 0), 0);
          setEditCostState(prev => ({
            editGrandTotalCost:
              prev.editGrandTotalCost - getWorksheetTotalCost(targetWorksheet!),
            editGrandTotalHours: !targetWorksheet?.rentPricing
              ?.isFlexibleShiftHr
              ? prev.editGrandTotalHours - nonWeeklyHoursDelta
              : prev.editGrandTotalHours,
          }));

          setSelectedWorksheetShifts(prev => {
            const newState = { ...prev };

            Object.keys(newState).forEach(key => {
              const entry = newState[key];

              if (entry.worksheet.cartId === targetWorksheet?.cartId) {
                const filteredShifts = entry.shifts.filter(
                  s => !validShifts.some((vs: Shift) => vs._id === s._id),
                );

                if (!filteredShifts.length) {
                  delete newState[key];
                } else {
                  newState[key] = {
                    worksheet: entry.worksheet,
                    shifts: filteredShifts,
                  };
                }
              }
            });

            return newState;
          });
        }

        return newSet;
      });
    },
    [displayData, getWorksheetShiftIds, selectedWorksheetIds],
  );
  function findWorksheetById(worksheetId: string) {
    // Search your displayData or source data for the correct worksheet object
    for (const cart of displayData) {
      const ws = cart.worksheets?.find((w: Worksheet) => w._id === worksheetId);
      if (ws) return ws;
    }
    return null;
  }
  // Handle shift selection
  const handleShiftSelection = useCallback(
    (shiftId: string, checked: boolean, worksheetId: string) => {
      setSelectedShiftIds(prev => {
        const newSet = new Set(prev);
        checked ? newSet.add(shiftId) : newSet.delete(shiftId);
        return newSet;
      });

      setSelectedWorksheetShifts(prev => {
        const newState = { ...prev };

        const shiftToAdd = findShiftById(shiftId, worksheetId);
        const worksheet = findWorksheetById(worksheetId);

        if (checked) {
          // Try to find existing worksheet key with same cartId
          const existingKey = Object.keys(newState).find(key => {
            const ws = newState[key]?.worksheet;
            return ws?.cartId === worksheet?.cartId;
          });

          if (existingKey && newState[existingKey]) {
            const existingShifts = newState[existingKey].shifts;

            // Add the shift only if not already present
            if (!existingShifts.some(s => s._id === shiftId)) {
              newState[existingKey] = {
                worksheet: newState[existingKey].worksheet,
                shifts: [...existingShifts!, shiftToAdd!],
              };
            }
          } else {
            // No matching cartId found, add new entry keyed by worksheetId
            newState[worksheetId] = {
              worksheet,
              shifts: [shiftToAdd!],
            };
          }

          const shiftCost = shiftToAdd?.isWeeklyShift
            ? 0
            : Number(shiftToAdd?.cost ?? shiftToAdd?.extraDayPrice ?? 0) +
              (shiftToAdd?.mileageCost ?? 0) +
              (shiftToAdd?.perDiemCost ?? 0) +
              (shiftToAdd?.rushFee ?? 0);
          const shiftHoursForTotal =
            !worksheet?.rentPricing?.isFlexibleShiftHr &&
            !shiftToAdd?.isWeeklyShift
              ? Number(shiftToAdd?.duration ?? 0)
              : 0;
          setEditCostState(prev => ({
            editGrandTotalCost: prev.editGrandTotalCost + shiftCost,
            editGrandTotalHours: prev.editGrandTotalHours + shiftHoursForTotal,
          }));
        } else {
          // Remove shift from selection keyed by worksheetId
          if (newState[worksheetId]) {
            newState[worksheetId].shifts = newState[worksheetId].shifts.filter(
              s => s._id !== shiftId,
            );
            // Remove entire worksheet if no shifts remain
            if (newState[worksheetId].shifts.length === 0) {
              delete newState[worksheetId];
            }
          }

          const shiftCost = shiftToAdd?.isWeeklyShift
            ? 0
            : Number(shiftToAdd?.cost ?? shiftToAdd?.extraDayPrice ?? 0) +
              (shiftToAdd?.mileageCost ?? 0) +
              (shiftToAdd?.perDiemCost ?? 0) +
              (shiftToAdd?.rushFee ?? 0);
          const shiftHoursForTotal =
            !worksheet?.rentPricing?.isFlexibleShiftHr &&
            !shiftToAdd?.isWeeklyShift
              ? Number(shiftToAdd?.duration ?? 0)
              : 0;
          setEditCostState(prev => ({
            editGrandTotalCost: prev.editGrandTotalCost - shiftCost,
            editGrandTotalHours: prev.editGrandTotalHours - shiftHoursForTotal,
          }));
        }

        return newState;
      });

      setCheckbox(prev => {
        const updatedArray = [...prev];
        const worksheet = findWorksheetById(worksheetId);
        const idx = prev.findIndex(item => item.itemId === worksheet.cartId);
        if (checked) {
          if (idx === -1) {
            // Add new item with shiftId
            updatedArray.push({
              itemId: worksheet.cartId,
              checkedIds: [shiftId],
            });
          } else {
            // Append shiftId if not present
            const existing = updatedArray[idx];
            if (!existing.checkedIds.includes(shiftId)) {
              const updatedIds = [...existing.checkedIds, shiftId];
              updatedArray[idx] = { ...existing, checkedIds: updatedIds };
            }
          }
        } else {
          if (idx !== -1) {
            // Remove shiftId from checkedIds
            const existing = updatedArray[idx];
            const updatedIds = existing.checkedIds.filter(
              (id: string) => id !== shiftId,
            );
            if (updatedIds.length === 0) {
              // Remove entire item if no shifts checked
              updatedArray.splice(idx, 1);
            } else {
              updatedArray[idx] = { ...existing, checkedIds: updatedIds };
            }
          }
        }

        return updatedArray;
      });
    },
    [findShiftById, findWorksheetById, selectedShiftIds, selectedWorksheetIds],
  );

  // Handle edit button click
  const handleEditClick = useCallback(() => {
    const worksheetIds = Object.keys(selectedWorksheetShifts);
    if (worksheetIds.length === 0) {
      // Nothing selected
      setIsModalOpen(false);
      setSelectedWorksheet(null);
      setSelectedShifts([]);
      return;
    }

    // Build fresh modal data from current selection
    const allSelectedShifts: any = [];
    let targetWorksheet = null;

    const firstWorksheetId = worksheetIds[0];
    for (const cart of displayData) {
      const found = cart.worksheets?.find(
        (w: Worksheet) => w._id === firstWorksheetId,
      );
      if (found) {
        targetWorksheet = found;
        break;
      }
    }

    Object.values(selectedWorksheetShifts).forEach(worksheetData => {
      allSelectedShifts.push(...worksheetData.shifts);
    });

    setSelectedWorksheet(targetWorksheet);
    setSelectedShifts(allSelectedShifts);
    setIsModalOpen(true);
  }, [selectedWorksheetShifts, displayData]);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setSelectedWorksheet(null);
    setSelectedShifts([]);
    setSelectedShiftIds(new Set());
    setSelectedWorksheetIds([]);
    setSelectedWorksheetShifts({});
    setIsModalOpen(false);
    setEditCostState({
      editGrandTotalCost: 0,
      editGrandTotalHours: 0,
    });
    setCheckbox([]);
  }, []);

  // Handle notes modal open
  const handleNotesModalOpen = useCallback((shift: any) => {
    setSelectedWorksheetForNotes(shift?.notes || []);
    setIsNotesModalOpen(true);
  }, []);

  // Handle notes modal close
  const handleNotesModalClose = useCallback(() => {
    setIsNotesModalOpen(false);
    // setSelectedWorksheetForNotes(null);
  }, []);

  const buildApiQuery = useCallback(
    (nextPage: number): string => {
      const fromDate = getStartDayTimestamp(cartDetails?.startTime);
      const toDate = getEndDayTimestamp(cartDetails?.endTime);
      return `limit=${ITEMS_PER_PAGE}&cartIds=${
        orderIds || '68973a05354621dc9cb5500e'
      }&pageNo=${nextPage}&toDate=${toDate}&fromDate=${fromDate}`;
    },
    [orderIds],
  );

  const loadMoreData = useCallback(async () => {
    if (loading || !hasMore || loader) return;

    // Check if all data loaded
    if (
      totalWorksheetsFromApi &&
      currentWorksheetCount >= totalWorksheetsFromApi
    ) {
      setHasMore(false);
      return;
    }

    const currentDataLength = displayData.length || 0;
    if (
      shiftPreviewData?.data?.total &&
      currentDataLength >= shiftPreviewData.data.total
    ) {
      setHasMore(false);
      return;
    }

    setLoading(true);

    try {
      setPage(prevPage => {
        const nextPage = prevPage + 1;
        const query = buildApiQuery(nextPage);
        getShiftPreviewData(query, true);
        return nextPage;
      });
    } catch (err) {
      console.error(err);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [
    loading,
    hasMore,
    loader,
    currentWorksheetCount,
    totalWorksheetsFromApi,
    displayData.length,
    shiftPreviewData?.data?.total,
    buildApiQuery,
  ]);

  const handleScroll = useCallback(() => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout for debouncing
    debounceTimeoutRef.current = setTimeout(() => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const { scrollTop, scrollHeight, clientHeight } = container;

      if (scrollHeight - scrollTop <= clientHeight + SCROLL_THRESHOLD) {
        loadMoreData();
      }
    }, DEBOUNCE_DELAY);
  }, [loadMoreData]);

  // Effects
  useEffect(() => {
    if (cartData) {
      // setPage(1);
      if (totalWorksheetsFromApi) {
        setHasMore(currentWorksheetCount < totalWorksheetsFromApi);
      } else {
        setHasMore(
          currentWorksheetCount > 0 && displayData?.length >= ITEMS_PER_PAGE,
        );
      }
    }
  }, [
    cartData,
    currentWorksheetCount,
    totalWorksheetsFromApi,
    displayData?.length,
  ]);

  useEffect(() => {
    if (
      totalWorksheetsFromApi &&
      currentWorksheetCount >= totalWorksheetsFromApi
    ) {
      setHasMore(false);
    }
  }, [currentWorksheetCount, totalWorksheetsFromApi]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => {
        container.removeEventListener('scroll', handleScroll);
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
      };
    }
  }, [handleScroll]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Memoized components
  const LoadingIndicator = useMemo(
    () => (
      <div
        style={{
          textAlign: 'center',
          padding: '20px',
          color: '#666',
        }}
      >
        <p>Loading more shifts...</p>
      </div>
    ),
    [],
  );
  const getWorksheetNonWeeklyDurationHours = (worksheet: any) => {
    const shifts = worksheet?.shifts ?? [];
    return shifts.reduce(
      (sum: number, s: any) =>
        sum + (s.isWeeklyShift ? 0 : Number(s?.duration ?? 0)),
      0,
    );
  };

  const { grandTotalCost, grandTotalHours } = useMemo(() => {
    let grandTotalCost = 0;
    let grandTotalHours = 0;
    const allWorksheets: any[] = [];

    displayData.forEach((cart: any) => {
      if (Array.isArray(cart.worksheets)) {
        cart.worksheets.forEach((worksheet: any) => {
          allWorksheets.push(worksheet);
          const worksheetTotalCost = getWorksheetTotalCost(worksheet);
          grandTotalCost += worksheetTotalCost;

          // totalDuration * totalUnit for each worksheet
          if (!worksheet?.rentPricing?.isFlexibleShiftHr) {
            grandTotalHours += getWorksheetNonWeeklyDurationHours(worksheet);
          }
        });
      }
    });
    return { grandTotalCost, grandTotalHours };
  }, [displayData]);

  /** Prefer the API's precomputed total; fall back to the client-side sum. */
  const displayGrandTotalCost =
    typeof apiTotalEstimateCost === 'number'
      ? apiTotalEstimateCost
      : grandTotalCost;

  /** Prefer the API's precomputed total; fall back to the client-side sum. */
  const displayGrandTotalHours =
    typeof apiTotalEstimateDuration === 'number'
      ? apiTotalEstimateDuration
      : grandTotalHours;

  const selectedWorksheetsForEdit = useMemo(
    () => Object.values(selectedWorksheetShifts).map((e: any) => e.worksheet),
    [selectedWorksheetShifts],
  );
  const displayEditGrandTotalCost = useMemo(
    () => editCostState.editGrandTotalCost,
    [selectedWorksheetsForEdit, editCostState.editGrandTotalCost],
  );

  const flatWorksheets = useMemo(
    () => displayData.flatMap((c: CartData) => c.worksheets || []),
    [displayData],
  );
  const showConsolidatedComboInfoIcons = useMemo(() => {
    if (!flatWorksheets.length) return false;
    const hasFlex = flatWorksheets.some(
      (w: Worksheet) => w.rentPricing?.isFlexibleShiftHr,
    );
    const hasFixed = flatWorksheets.some(
      (w: Worksheet) => !w.rentPricing?.isFlexibleShiftHr,
    );
    const hasWeeklyShift = flatWorksheets.some((w: Worksheet) =>
      w.shifts?.some((s: any) => s.isWeeklyShift),
    );
    const hasNonWeeklyShift = flatWorksheets.some((w: Worksheet) =>
      w.shifts?.some((s: any) => !s.isWeeklyShift),
    );
    return (hasFlex && hasFixed) || (hasWeeklyShift && hasNonWeeklyShift);
  }, [flatWorksheets]);

  const showEditComboInfoIcons = useMemo(() => {
    if (!selectedShiftIds.size) return false;
    let sawWeeklySel = false;
    let sawNonWeeklySel = false;
    const pricingKinds = new Set<'flex' | 'fixed'>();
    for (const cart of displayData) {
      for (const ws of cart.worksheets ?? []) {
        let hasSel = false;
        for (const s of ws.shifts ?? []) {
          if (!selectedShiftIds.has(s._id)) continue;
          hasSel = true;
          if ((s as any).isWeeklyShift) sawWeeklySel = true;
          else sawNonWeeklySel = true;
        }
        if (hasSel) {
          pricingKinds.add(
            ws.rentPricing?.isFlexibleShiftHr ? 'flex' : 'fixed',
          );
        }
      }
    }
    return (
      (sawWeeklySel && sawNonWeeklySel) ||
      (pricingKinds.has('flex') && pricingKinds.has('fixed'))
    );
  }, [displayData, selectedShiftIds]);

  const showConsolidatedHoursInfo = useMemo(() => {
    if (!flatWorksheets.length) return false;
    const hasFlex = flatWorksheets.some(
      (w: Worksheet) => w.rentPricing?.isFlexibleShiftHr,
    );
    const hasFixed = flatWorksheets.some(
      (w: Worksheet) => !w.rentPricing?.isFlexibleShiftHr,
    );
    const weeklyOnFixed = flatWorksheets.some(
      (w: Worksheet) =>
        !w.rentPricing?.isFlexibleShiftHr &&
        w.shifts?.some((s: any) => s.isWeeklyShift),
    );
    return (hasFlex && hasFixed) || weeklyOnFixed;
  }, [flatWorksheets]);

  /** Show "Flexible" label only when any shift/worksheet is truly flexible (view). */
  const hasAnyFlexibleShiftInView = useMemo(() => {
    return flatWorksheets.some(
      (w: any) =>
        Boolean(w?.rentPricing?.isFlexibleShiftHr) ||
        (w?.shifts ?? []).some((s: any) => Boolean(s?.isFLexibleShiftHr)),
    );
  }, [flatWorksheets]);

  /** Show "Flexible" label only when selection includes any flexible shift/worksheet (edit). */
  const hasAnyFlexibleShiftInEditSelection = useMemo(() => {
    if (!selectedShiftIds.size) return false;
    for (const cart of displayData) {
      for (const ws of cart.worksheets ?? []) {
        const wsIsSelected = (ws.shifts ?? []).some((s: any) =>
          selectedShiftIds.has(s._id),
        );
        if (!wsIsSelected) continue;
        if (ws?.rentPricing?.isFlexibleShiftHr) return true;
        for (const s of ws.shifts ?? []) {
          if (!selectedShiftIds.has(s._id)) continue;
          if (s?.isFLexibleShiftHr) return true;
        }
      }
    }
    return false;
  }, [displayData, selectedShiftIds]);

  const showEditConsolidatedHoursInfo = useMemo(() => {
    const wss = selectedWorksheetsForEdit;
    if (!wss?.length) return false;
    const hasFlex = wss.some(
      (w: Worksheet) => w.rentPricing?.isFlexibleShiftHr,
    );
    const hasFixed = wss.some(
      (w: Worksheet) => !w.rentPricing?.isFlexibleShiftHr,
    );
    const weeklyOnFixed = wss.some(
      (w: Worksheet) =>
        !w.rentPricing?.isFlexibleShiftHr &&
        w.shifts?.some((s: any) => s.isWeeklyShift),
    );
    return (hasFlex && hasFixed) || weeklyOnFixed;
  }, [selectedWorksheetsForEdit]);

  const allDisplayedShiftsAreWeekly = useMemo(() => {
    const shifts = flatWorksheets.flatMap((w: Worksheet) => w.shifts ?? []);
    if (!shifts.length) return false;
    return shifts.every((s: any) => s.isWeeklyShift);
  }, [flatWorksheets]);

  const editSelectionIncludesWeeklyShift = useMemo(() => {
    if (!selectedShiftIds.size) return false;
    for (const cart of displayData) {
      for (const ws of cart.worksheets ?? []) {
        for (const s of ws.shifts ?? []) {
          if (selectedShiftIds.has(s._id) && (s as any).isWeeklyShift) {
            return true;
          }
        }
      }
    }
    return false;
  }, [displayData, selectedShiftIds]);

  const handleDateSelection = (
    e: React.ChangeEvent<HTMLInputElement>,
    worksheet: Worksheet[],
  ) => {
    worksheet?.forEach(val => {
      if (e.target.checked) {
        handleWorksheetSelection(val?._id, true);
      } else {
        handleWorksheetSelection(val?._id, false);
      }
    });
  };

  const showCheckBoxOnDate = useCallback(
    (data: Worksheet[]) => {
      const now = moment();
      return (
        isEditMode &&
        data?.some(val => now.isSameOrBefore(moment(val?.lastShift)))
      );
    },
    [isEditMode],
  );

  const filteredShifts = useCallback(
    (data: Worksheet[]) => {
      const shifts: Shift[] = [];
      data?.map((worksheet: Worksheet) => {
        // Filter shifts to only future or ongoing ones
        const validShifts = worksheet.shifts?.filter((shift: Shift) =>
          moment(shift.actualEndTime).isSameOrAfter(moment()),
        );

        // Return worksheet only if it has valid shifts
        if (validShifts && validShifts.length > 0) {
          shifts.push(...validShifts);
        }

        return null;
      });

      return shifts;
    },
    [showCheckBoxOnDate, isEditMode],
  );

  console.log(cartDetails, 'previewShiftDetails');

  return (
    <div className="ScheduledServices">
      <div className="ScheduledServicesHeading">
        {cartDetails && displayData?.length > 0 && (
          <div className="flex between">
            <div className="d-flex gap10">
              <h3 className="fw600 MuiTypography-h6 mb-4 flex align-center">
                {`${cartDetails?.setName?.toUpperCase()}${
                  cartDetails?.episodeName
                    ? ` || ${
                        cartDetails?.episodeNumber &&
                        cartDetails?.episodeNumber !== 'undefined'
                          ? `${cartDetails?.episodeNumber} - `
                          : ''
                      }${cartDetails?.episodeName}`
                    : ''
                }` || 'Shift Details'}
              </h3>
            </div>
            {displayData?.length > 0 &&
              (!isEditMode ? (
                <div>
                  <p className="MuiTypography-body2 fw600 tag-text d-inline-flex align-center flex-wrap">
                    <span className="d-inline-flex align-center">
                      {displayGrandTotalCost > 0
                        ? `$${formatPriceBreakupValues(displayGrandTotalCost)}`
                        : allDisplayedShiftsAreWeekly
                        ? 'Included'
                        : `$${formatPriceBreakupValues(displayGrandTotalCost)}`}
                      {showConsolidatedComboInfoIcons ? (
                        <ToolTipComponent
                          title={SHIFT_DETAILS_CONSOLIDATED_PRICE_TOOLTIP}
                          arrow
                          theme="light"
                        >
                          <i
                            className="info-icon ml-6"
                            style={{ cursor: 'pointer' }}
                            aria-label="Consolidated price info"
                          />
                        </ToolTipComponent>
                      ) : null}
                    </span>{' '}
                    <span className="d-inline-flex align-center">
                      {hasAnyFlexibleShiftInView
                        ? 'Flexible'
                        : `${displayGrandTotalHours}hrs`}
                      {showConsolidatedHoursInfo ? (
                        <ToolTipComponent
                          title={SHIFT_DETAILS_CONSOLIDATED_HOURS_TOOLTIP}
                          arrow
                          theme="light"
                        >
                          <i
                            className="info-icon ml-6"
                            style={{ cursor: 'pointer' }}
                            aria-label="Consolidated hours info"
                          />
                        </ToolTipComponent>
                      ) : null}
                    </span>
                  </p>
                </div>
              ) : (
                selectedShiftIds.size > 0 &&
                (displayEditGrandTotalCost > 0 ||
                  editSelectionIncludesWeeklyShift) && (
                  <div>
                    <p className="MuiTypography-body2 fw600 tag-text d-inline-flex align-center flex-wrap">
                      <span className="d-inline-flex align-center">
                        {displayEditGrandTotalCost > 0
                          ? `$${formatPriceBreakupValues(
                              displayEditGrandTotalCost,
                            )}`
                          : 'Included'}
                        {showEditComboInfoIcons ? (
                          <ToolTipComponent
                            title={SHIFT_DETAILS_CONSOLIDATED_PRICE_TOOLTIP}
                            arrow
                            theme="light"
                          >
                            <i
                              className="info-icon ml-6"
                              style={{ cursor: 'pointer' }}
                              aria-label="Consolidated price info"
                            />
                          </ToolTipComponent>
                        ) : null}
                      </span>{' '}
                      <span className="d-inline-flex align-center">
                        {hasAnyFlexibleShiftInEditSelection
                          ? 'Flexible'
                          : `${editCostState.editGrandTotalHours}hrs`}
                        {showEditConsolidatedHoursInfo ? (
                          <ToolTipComponent
                            title={SHIFT_DETAILS_CONSOLIDATED_HOURS_TOOLTIP}
                            arrow
                            theme="light"
                          >
                            <i
                              className="info-icon ml-6"
                              style={{ cursor: 'pointer' }}
                              aria-label="Consolidated hours info"
                            />
                          </ToolTipComponent>
                        ) : null}
                      </span>
                    </p>
                  </div>
                )
              ))}
          </div>
        )}
      </div>

      <div
        ref={scrollContainerRef}
        style={{ maxHeight: MAX_HEIGHT, overflowY: 'auto' }}
        className="shifts-scroll-container"
      >
        {displayData?.length > 0 &&
        displayData.find((val: CartData) => val.worksheets?.length > 0) ? (
          displayData.map((cart: CartData) => (
            <React.Fragment key={cart._id}>
              {cart.worksheets?.filter(
                (val: Worksheet) => val.shifts?.length > 0,
              )?.length > 0 && (
                <div className="serviceDate">
                  <p className="flex align-center">
                    {showCheckBoxOnDate(cart.worksheets) && (
                      <div className="checkbox">
                        <input
                          type="checkbox"
                          checked={
                            Array.from(selectedShiftIds)?.filter(
                              (val: string) =>
                                cart?.worksheets?.find(worksheet =>
                                  worksheet.shifts?.find(
                                    (shift: Shift) => shift._id === val,
                                  ),
                                ),
                            )?.length >=
                            filteredShifts(cart?.worksheets)?.length
                          }
                          onChange={e =>
                            handleDateSelection(e, cart?.worksheets)
                          }
                          onClick={e => e.stopPropagation()}
                          ref={el => {
                            if (el) {
                              el.indeterminate =
                                Array.from(selectedShiftIds)?.filter(
                                  (val: string) =>
                                    cart?.worksheets?.find(worksheet =>
                                      worksheet.shifts?.find(
                                        (shift: Shift) => shift._id === val,
                                      ),
                                    ),
                                )?.length <
                                  filteredShifts(cart?.worksheets)?.length &&
                                Array.from(selectedShiftIds)?.filter(
                                  (val: string) =>
                                    cart?.worksheets?.find(worksheet =>
                                      worksheet.shifts?.find(
                                        (shift: Shift) => shift._id === val,
                                      ),
                                    ),
                                )?.length > 0;
                            }
                          }}
                        />
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label aria-label="Select all shifts"></label>
                      </div>
                    )}
                    {formatServiceDate(cart.worksheets[0]?.firstShift)}
                  </p>
                </div>
              )}
              {cart.worksheets?.map((worksheet: Worksheet) => {
                if (!worksheet.shifts.length) return null;
                return (
                  <WorksheetItem
                    key={worksheet._id}
                    worksheet={worksheet}
                    expandedAccordion={expandedAccordion}
                    isEditMode={isEditMode}
                    product={product}
                    selectedShiftIds={selectedShiftIds}
                    filteredShifts={filteredShifts}
                    handleWorksheetSelection={handleWorksheetSelection}
                    handleToggle={handleToggle}
                    handleShiftSelection={handleShiftSelection}
                    handleNotesModalOpen={handleNotesModalOpen}
                    setAdditionalShifts={setAdditionalShifts}
                    setAdditionalShiftsData={setAdditionalShiftsData}
                  />
                );
              })}
            </React.Fragment>
          ))
        ) : (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            <h3>Result not found</h3>
            <p>
              We&apos;re sorry we couldn&apos;t find what you were looking for.
              Please try another keyword.
            </p>
          </div>
        )}

        {(loading || loader) && LoadingIndicator}
        {/* {!hasMore &&
          displayData.length > 0 &&
          !loading &&
          !loader &&
          NoMoreDataIndicator} */}
      </div>

      {isEditMode && displayData?.length > 0 && (
        <div className="cart-lower-btns btn-grp">
          <div>
            <CustomButton
              text="Cancel"
              variant="outlined"
              onClick={() => {
                setIsEditMode(false);
                setSelectedShiftIds(new Set());
                setSelectedWorksheetShifts({});
                setSelectedWorksheetIds([]);
                setCheckbox([]);
                setEditCostState({
                  editGrandTotalCost: 0,
                  editGrandTotalHours: 0,
                });
              }}
              showIcon={false}
              id="cancel-shift"
              size="large"
            />
          </div>
          {onDeleteShiftsClick != null && (
            <div>
              <CustomButton
                text={`Delete ${totalShifts > 1 ? 'Shifts' : 'Shift'}`}
                variant="contained"
                color="error"
                onClick={onDeleteShiftsClick}
                disabled={totalShifts === 0}
                showIcon={false}
                id="delete-shift"
                size="large"
              />
            </div>
          )}
          <div>
            <CustomButton
              text="Edit"
              size="large"
              variant="contained"
              onClick={handleEditClick}
              disabled={selectedShiftIds.size === 0}
              showIcon={false}
              id="save-shift"
            />
          </div>
        </div>
      )}

      {/* Edit Shift Modal */}
      <EditShiftModal
        open={isModalOpen}
        onClose={handleModalClose}
        selectedWorksheet={selectedWorksheet || undefined}
        selectedShifts={selectedShifts}
        cartDetails={cartDetails}
        buildApiQuery={buildApiQuery}
        setSelectedWorkSheet={setSelectedWorksheet}
        selectedWorksheetShifts={selectedWorksheetShifts}
        setSelectedDate={setSelectedDate}
        displayData={displayData}
      />

      {/* View Notes Modal */}
      <ViewNotesModal
        open={isNotesModalOpen}
        onClose={handleNotesModalClose}
        notes={selectedWorksheetForNotes || []}
        worksheetTitle={
          selectedWorksheetForNotes?.productInfo?.title || 'Worksheet Notes'
        }
      />
      <AdditionalShifts
        open={additionalShifts}
        onClose={() => setAdditionalShifts(false)}
        additionalShiftsData={additionalShiftsData}
      />
      <AdditionalChargesModal
        open={additionalChargesOpen}
        onClose={() => setAdditionalChargesOpen(false)}
        shifts={additionalChargesShifts}
      />
    </div>
  );
}
